import React from 'react'
import { useQuery } from 'react-query'
import { useSearchParams, Link } from 'react-router-dom'

import Button from 'components/Button'
import { postReportVoucherRedemptionCountService } from 'api/reports-voucher-redemption-count'
import { ExportToCSV } from '../../../reports-list/utils-reports-actions'

type VoucherIssue = { voucherId: number; issueCount: number }
type VoucherRedemptionItem = { unit: string; voucherId: number; redemptionCount: number }

const PieChart = ({
  data,
  size = 160,
  centerText,
}: {
  data: { label: string; value: number; color?: string }[]
  size?: number
  centerText?: string
}) => {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return null
  let cumulative = 0
  const radius = Math.max(40, Math.floor(size / 2) - 8)
  const cx = Math.floor(size / 2)
  const cy = Math.floor(size / 2)

  const sectorPath = (start: number, end: number) => {
    const startRad = (start / total) * Math.PI * 2 - Math.PI / 2
    const endRad = (end / total) * Math.PI * 2 - Math.PI / 2
    const x1 = cx + radius * Math.cos(startRad)
    const y1 = cy + radius * Math.sin(startRad)
    const x2 = cx + radius * Math.cos(endRad)
    const y2 = cy + radius * Math.sin(endRad)
    const large = end - start > total / 2 ? 1 : 0
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.length === 1 ? (
        (() => {
          const d = data[0]
          const color = d.color || `hsl(200 70% 50%)`
          return <circle cx={cx} cy={cy} r={radius} fill={color} stroke="#fff" />
        })()
      ) : (
        data.map((d) => {
          const start = cumulative
          cumulative += d.value
          const end = cumulative
          const path = sectorPath(start, end)
          const color = d.color || `hsl(200 70% 50%)`
          return <path key={d.label} d={path} fill={color} stroke="#fff" />
        })
      )}
      {centerText && (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={12} fill="#111">
          {centerText}
        </text>
      )}
    </svg>
  )
}

export const ViewVoucherRedemption = () => {
  const [searchParams] = useSearchParams()
  const searchQuery = JSON.parse(decodeURIComponent(searchParams.get('report-query')!))

  const { data, isLoading, isError } = useQuery(['voucherRedemptionReport', searchQuery], () => postReportVoucherRedemptionCountService(searchQuery))

  if (isLoading) return <div>Loading...</div>
  if (isError) return <div>Error loading report</div>

  const apiData = data?.data

  const issueCounts = apiData?.voucherIssueCounts || []
  const redemptionCounts = apiData?.voucherRedemptionCounts || []

  const redemptionByVoucher = redemptionCounts.reduce<Record<number, number>>((acc, item: VoucherRedemptionItem) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.redemptionCount
    return acc
  }, {})

  const issueByVoucher = issueCounts.reduce<Record<number, number>>((acc, item: VoucherIssue) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.issueCount
    return acc
  }, {})

  const combinedVoucherIds = Array.from(new Set([...Object.keys(issueByVoucher).map(Number), ...Object.keys(redemptionByVoucher).map(Number)]))
  const csvRows = combinedVoucherIds.map((vid) => ({
    voucherId: vid,
    issued: issueByVoucher[vid] || 0,
    redeemed: redemptionByVoucher[vid] || 0,
  }))

  return (
    <div className="my-6">
      <div className="flex items-center gap-4">
        <button className="px-3 py-1 border rounded-md border-primary text-primary" type="button">
          <Link to={`/reports-list?modal=voucher_redemption`} className="flex items-center gap-2">
            <span className="tracking-wider font-light">Back to reports</span>
          </Link>
        </button>

        <Button
          type="button"
          className="ml-1 rounded-lg tracking-wide font-bold"
          iconRight={false}
          onClick={() => ExportToCSV(csvRows, { filename: 'Voucher-Issues-vs-Redemptions' })}
        >
          Download CSV Report
        </Button>
      </div>

      <br />

      <div className="flex gap-10 items-start">
        <div>
          <h4 className="font-bold">Redemptions by Voucher</h4>
          {Object.keys(redemptionByVoucher).length ? (
            <table className="table-auto border-collapse">
              <thead>
                <tr>
                  <th className="px-2 pl-0">Voucher ID</th>
                  <th className="px-2">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(redemptionByVoucher).map(([id, count]) => (
                  <tr key={id}>
                    <td className="px-2 pl-0">{id}</td>
                    <td className="px-2">{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No redemption data available</p>
          )}
        </div>

        <div>
          <h4 className="font-bold">Issue Counts</h4>
          <table className="table-auto border-collapse">
            <thead>
              <tr>
                <th className="px-2 pl-0">Voucher ID</th>
                <th className="px-2">Issued</th>
              </tr>
            </thead>
            <tbody>
              {issueCounts.map((row: VoucherIssue) => (
                <tr key={row.voucherId}>
                  <td className="px-2 pl-0">{row.voucherId}</td>
                  <td className="px-2">{row.issueCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overall Issued vs Redeemed chart removed — per-voucher charts only */}

      <div className="mt-8">
        <h4 className="font-bold">Per Voucher Charts</h4>
        {combinedVoucherIds.length === 0 ? (
          <p className="mt-2">No voucher data available</p>
        ) : (
          <div className="flex gap-6 flex-wrap">
            {combinedVoucherIds.slice(0, 5).map((vid) => {
              const issued = issueByVoucher[vid] || 0
              const redeemed = redemptionByVoucher[vid] || 0
              const chartData = [] as { label: string; value: number; color?: string }[]
              // issued = red, redeemed = blue
              if (issued > 0) chartData.push({ label: 'Issued', value: issued, color: '#e53e3e' })
              if (redeemed > 0) chartData.push({ label: 'Redeemed', value: redeemed, color: '#3182ce' })

              return (
                <div key={vid} className="w-44 bg-white p-2 rounded shadow-sm">
                  <div className="flex justify-center">
                    {chartData.length ? (
                      <PieChart data={chartData} size={140} />
                    ) : (
                      <div className="text-center text-sm text-gray-600">No data</div>
                    )}
                  </div>
                  <div className="mt-2 text-center text-sm font-bold">{vid}</div>
                  <div className="mt-3 flex justify-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#e53e3e', display: 'inline-block' }} />
                      <span>Issued</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#3182ce', display: 'inline-block' }} />
                      <span>Redeemed</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewVoucherRedemption
